import { useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  approvalsGetApprovalOptions,
  transferCheckDepositbatchGetBatchOptions,
  transferCheckReviewTransfer,
} from '@column/api';
import { Button } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import {
  CheckPreviewQuickLook,
  CheckPreviewThumbnails,
  useCheckPreviewPages,
} from '~/app/pages/Transfers/_components/CheckPreviewQuickLook';
import { CheckReturnModal } from '~/app/pages/Transfers/_components/CheckReturnModal';
import { buildCheckEvents, buildCheckReturnEvents } from '~/app/pages/Transfers/_utils/check';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, RenderFields } from '~/components';
import { useAccountNumber, useBankAccount, useEntity } from '~/hooks';
import { useTransfer } from '~/hooks/useTransfers';
import { useOptimisticTransferUpdate } from '~/lib/api';
import { useNavigate } from '~/lib/navigation';
import { BatchTransfersSheet, TransferHeader } from '~/lib/transfers';
import { CheckRepository, CheckReturnsList, CheckTransfer, convertTransferAmount } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Divider, Inner } from '~/styles';
import { ReviewTransferRequest } from '~/typings/enum';

import { InfoForm } from './_components/Layout';
import { Timeline } from './_components/Timeline';
import { TransferReview } from './_components/TransferReview';
import { isApprovalCandidateStatus, isApprovalHold, submitApprovalHold } from './_utils/transferReview';

interface Params {
  id: string;
}

const formatData = (transfer: CheckTransfer): Record<string, unknown> => {
  const formatted: Record<string, unknown> = {
    micrLine: transfer.micrLine
      ? {
          amountField: transfer.micrLine.amountField || null,
          payorBankRoutingNumber: transfer.micrLine.payorBankRoutingNumber || null,
          auxiliaryOnUs: transfer.micrLine.auxiliaryOnUs || null,
          onUs: transfer.micrLine.onUs || null,
          externalProcessingCode: transfer.micrLine.externalProcessingCode || null,
        }
      : null,
    payeeAddress: transfer.payeeAddress
      ? {
          line1: transfer.payeeAddress.line1?.toString() || null,
          line2: transfer.payeeAddress.line2?.toString() || null,
          city: transfer.payeeAddress.city?.toString() || null,
          state: transfer.payeeAddress.state?.toString() || null,
          postalCode: transfer.payeeAddress.postalCode?.toString() || null,
          countryCode: transfer.payeeAddress.countryCode?.toString() || null,
        }
      : null,
  };

  Object.entries(transfer).forEach(([key, value]) => {
    if (key.startsWith('payee') || key.startsWith('check') || key.startsWith('delivery')) {
      return;
    }
    formatted[key] = value;
  });

  return formatted;
};

export const PageTransfersCheck: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const navigate = useNavigate();
  const { currentUser, isAuthorized, isSandbox } = useSessionStore();
  const [isReturnModalOpen, setReturnModalOpen] = useState(false);
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();

  const {
    response: bankAccount,
    isLoading: isBankAccountLoading,
    createRequest: fetchBankAccount,
  } = useBankAccount({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const {
    response: accountNumber,
    isLoading: isAccountNumberLoading,
    createRequest: fetchAccountNumber,
  } = useAccountNumber({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const { response: entity, createRequest: fetchEntity } = useEntity({
    onSuccess: () => setIsLoadingEntity(false),
    onError: (error) => {
      setIsLoadingEntity(false);
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const [checkReturnsList, setCheckReturnsList] = useState<CheckReturnsList['returns'] | undefined>([]);
  const {
    response: transfer,
    isLoading: isTransferLoading,
    createRequest: fetchTransfer,
  } = useTransfer<CheckTransfer>('check')({
    initialParams: { id },
    onSuccess: async (response) => {
      fetchAccountNumber({ id: response.accountNumberId });

      try {
        if (response.bankAccountId) {
          const currentBankAccount = await fetchBankAccount({ id: response.bankAccountId });

          if (!currentBankAccount) {
            return;
          }

          if (isAuthorized({ permission: 'entities', permissionLevel: 'read' })) {
            fetchEntity({ id: currentBankAccount.owners?.[0] });
            setIsLoadingEntity(true);
          }
        }
      } catch (error) {
        addDangerNotification({ content: (error as Error).message });
      }

      CheckRepository.listReturns(id)
        .then((returns) => {
          if (returns.returns && returns.returns.length > 0) {
            setCheckReturnsList(returns.returns);
          }
        })
        .catch(() => {
          setCheckReturnsList(undefined);
        });
    },
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(ROUTE.TRANSFERS);
    },
  });

  const checkDepositBatchId = transfer?.type?.toLowerCase() === 'credit' ? transfer.transferBatchId : undefined;
  const {
    data: checkDepositBatch,
    isLoading: isTransferBatchLoading,
    error: checkDepositBatchError,
  } = useQuery({
    ...transferCheckDepositbatchGetBatchOptions({ path: { check_deposit_batch_id: checkDepositBatchId ?? '' } }),
    enabled: !!checkDepositBatchId,
  });
  useEffect(() => {
    if (checkDepositBatchError) {
      addDangerNotification({ content: checkDepositBatchError.message });
    }
  }, [checkDepositBatchError, addDangerNotification]);

  const formattedTransfer = useMemo(() => (transfer ? formatData(transfer) : undefined), [transfer]);
  const isApprovalCandidate = isApprovalCandidateStatus(transfer?.status);
  const { data: approvalState } = useQuery({
    ...approvalsGetApprovalOptions({ path: { object_id: id } }),
    enabled: isApprovalCandidate,
  });
  const isPendingApproval = isApprovalCandidate && approvalState?.status === 'pending';
  const isMarkedApprovalHold = isPendingApproval && isApprovalHold(transfer?.status);
  const [isReviewLoading, setReviewLoading] = useState(false);
  const [isPreviewOpen, setPreviewOpen] = useState(false);
  const [previewPDFFileUrl, setPreviewPDFFileUrl] = useState<string>();
  const optimisticallyUpdateTransfer = useOptimisticTransferUpdate();
  const {
    data: previewPDF,
    error: previewPDFError,
    isLoading: isPreviewPDFLoading,
  } = useQuery({
    queryKey: ['checkTransfer', 'previewPDF', currentUser?.defaultPlatformId, isSandbox, id],
    queryFn: () => CheckRepository.previewPDF(id),
    enabled: Boolean(transfer?.isPreviewPdfAvailable && bankAccount && accountNumber && !isLoadingEntity),
  });
  useEffect(() => {
    if (!previewPDF) {
      setPreviewPDFFileUrl(undefined);
      return;
    }

    const fileUrl = URL.createObjectURL(previewPDF);
    setPreviewPDFFileUrl(fileUrl);
    return () => URL.revokeObjectURL(fileUrl);
  }, [previewPDF]);
  useEffect(() => {
    if (previewPDFError) {
      addDangerNotification({
        content: previewPDFError.message ?? 'An error occurred while loading the check preview PDF.',
      });
    }
  }, [addDangerNotification, previewPDFError]);
  const { hasRenderError, isRendering, pages: previewPDFPages } = useCheckPreviewPages(previewPDFFileUrl);
  const reviewTransfer = useCallback(
    async (req: ReviewTransferRequest) => {
      setReviewLoading(true);
      try {
        if (isApprovalHold(transfer?.status)) {
          const updatedTransfer = await submitApprovalHold({ id, request: req, transferType: 'check_debit' });
          optimisticallyUpdateTransfer({ id: updatedTransfer.id, status: updatedTransfer.status });
        } else {
          const { data: reviewedTransfer } = await transferCheckReviewTransfer({
            path: { check_transfer_id: id },
            body: { comment: req.comment, decision: req.decision },
            throwOnError: true,
          });
          optimisticallyUpdateTransfer({ id: reviewedTransfer.id, status: reviewedTransfer.status });
        }
        await fetchTransfer();
      } catch (err) {
        addDangerNotification({
          content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
        });
      } finally {
        setReviewLoading(false);
      }
    },
    [addDangerNotification, fetchTransfer, id, optimisticallyUpdateTransfer]
  );

  const formMethods = useForm<CheckTransfer>({ values: transfer });
  useEffect(() => {
    if (transfer) {
      formMethods.reset();
    }
  }, [transfer]);

  const isDataLoading = useMemo(() => {
    return (
      isAccountNumberLoading ||
      isBankAccountLoading ||
      isTransferLoading ||
      (transfer?.transferBatchId && transfer?.type?.toLowerCase() === 'credit' && isTransferBatchLoading) ||
      !transfer
    );
  }, [isAccountNumberLoading, isBankAccountLoading, isTransferLoading, isTransferBatchLoading, transfer]);

  const handleStopPayment = useCallback(() => {
    if (!transfer) return;
    CheckRepository.stopPayment(transfer.id)
      .then(() => {
        fetchTransfer();
        addSuccessNotification({
          content: 'Check payment stopped',
        });
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  }, [transfer, fetchTransfer, addSuccessNotification, addDangerNotification]);

  const transferEntryType = transfer?.type.toLowerCase() === 'credit' ? 'check_credit' : 'check_debit';
  const isIncoming = transfer?.type.toLowerCase() === 'credit';

  return (
    <>
      {transfer && (
        <CheckReturnModal
          isOpen={isReturnModalOpen}
          onClose={() => setReturnModalOpen(false)}
          transferId={transfer.id}
        />
      )}
      <CheckPreviewQuickLook
        hasRenderError={hasRenderError}
        isLoading={isPreviewPDFLoading}
        isOpen={isPreviewOpen}
        isRendering={isRendering}
        onOpenChange={setPreviewOpen}
        pages={previewPDFPages}
        pdfFileName={`${id}-preview.pdf`}
        pdfFileUrl={previewPDFFileUrl}
      />

      <PageActions>
        {(transfer?.status === 'issued' || transfer?.status === 'manual_review') && (
          <Button variant="danger" size="small" onClick={handleStopPayment}>
            Stop Payment
          </Button>
        )}
        {transfer?.status === 'settled' && transfer?.type.toLowerCase() !== 'credit' && (
          <Button variant="secondary" onClick={() => setReturnModalOpen(true)} size="small">
            Return
          </Button>
        )}
      </PageActions>

      <Loading isLoading={isDataLoading} fullHeight>
        {transfer && (
          <TransferHeader
            amount={transfer.depositedAmount || transfer.positivePayAmount || '0'}
            createdAt={transfer.createdAt}
            isAdding={transfer ? convertTransferAmount(transferEntryType, isIncoming) > 0 : undefined}
            direction={{
              type: isIncoming ? 'incoming' : 'outgoing',
              label: isIncoming ? 'Deposit' : 'Issue',
            }}
            status={isMarkedApprovalHold ? 'pre_review' : transfer.status}
            transferId={transfer.id}
            transferType={transferEntryType}
          />
        )}
        <TransferReview
          amount={transfer?.positivePayAmount || transfer?.depositedAmount || undefined}
          bankAccountName={bankAccount?.displayName || bankAccount?.description || 'Unnamed Bank Account'}
          counterpartyLabel="Payee"
          counterpartyName={transfer?.payeeName || 'Unnamed Payee'}
          createdByName={transfer?.createdBy?.name}
          description={transfer?.description || undefined}
          isDisabled={
            !isAuthorized({ permission: 'approveTransfers', permissionLevel: 'write', bankAccount }) ||
            !approvalState?.can_review
          }
          isLoading={isReviewLoading}
          isOpen={!!transfer && isPendingApproval}
          onSubmit={reviewTransfer}
        />
        {bankAccount && accountNumber && transfer && !isLoadingEntity && (
          <Timeline
            entries={buildCheckEvents({
              transfer,
              batch: checkDepositBatch,
              account: {
                accountNumber,
                bankAccount,
                entityType: entity ? (entity.type.toLowerCase() as 'person' | 'business') : undefined,
              },
              images: {
                frontImage: transfer.frontImage || undefined,
                backImage: transfer.backImage || undefined,
              },
              previewPDFAction: transfer.isPreviewPdfAvailable ? (
                <CheckPreviewThumbnails
                  hasRenderError={hasRenderError}
                  isLoading={isPreviewPDFLoading}
                  isRendering={isRendering}
                  onOpen={() => setPreviewOpen(true)}
                  pages={previewPDFPages}
                />
              ) : undefined,
            })}
            returnEntries={buildCheckReturnEvents(checkReturnsList)}
          />
        )}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<CheckTransfer>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                    {
                      id: 'idempotencyKey',
                      label: 'Idempotency Key',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} isMonospaced />;
                      },
                    },
                    {
                      id: 'checkNumber',
                      label: 'Check Number',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                  ],
                },
                {
                  headline: 'MICR Line',
                  fields: [
                    {
                      id: 'micrLine.amountField',
                      label: 'Amount Field',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                    {
                      id: 'micrLine.payorBankRoutingNumber',
                      label: 'Payor Bank Routing Number',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                    {
                      id: 'micrLine.auxiliaryOnUs',
                      label: 'Auxiliary On Us',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                    {
                      id: 'micrLine.onUs',
                      label: 'On Us',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                    {
                      id: 'micrLine.externalProcessingCode',
                      label: 'External Processing Code',
                      children: ({ value }) => {
                        return <CopyInput value={value ? String(value) : '-'} />;
                      },
                    },
                  ],
                },
              ],
            })}
            <Divider />
            <Inner px={0} pt={24}>
              <Details
                label="Transfer Details"
                formattedData={formattedTransfer}
                rawData={transfer as Record<string, unknown> | undefined}
              />
            </Inner>
          </InfoForm>
        </FormProvider>
      </Loading>
      <BatchTransfersSheet />
    </>
  );
};
